import React, { useMemo, useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { connect } from "react-redux";
import Codex from "@blumira/blu_constants";
import Schema from "lib/schema";
import {
  useQueryControls,
  useResultControls,
  SaveRequestModal,
  decodeKey,
} from "views/Components/QueryControls";
import PropTypes from "prop-types";

import DataSourcePicker from "views/Components/Reporting/DataSourcePicker";
import FilterEditor from "views/Components/Reporting/FilterEditor";
import OutputFieldsEditor from "views/Components/Reporting/OutputFieldsEditor";
import SavedQueryModal from "views/Components/Reporting/SavedQueryModal";
import DataTable from "views/Components/DataTable";
import { TimeControls } from "views/Components/TimespanPicker";
import LicenseRestriction from "views/Components/License";

import {
  Card,
  Grid,
  Menu,
  Switch,
  Button,
  Tooltip,
  Collapse,
  MenuItem,
  FormGroup,
  Typography,
  IconButton,
  FormControl,
  CardContent,
  ButtonGroup,
  FormControlLabel,
} from "@material-ui/core";

import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";

import Alert from "@material-ui/lab/Alert";

// ** Selectors ***********************
import { loadPageData } from "redux/actions/Request";
import _ from "lodash";
import Request from "../../../lib/api/Request";
import { useDeepCompareEffect } from "react-use";
import { getValue } from "utils";

import { copyTextToClipboard } from "utils";
import { isDateTimeField } from "views/Components/Reporting/FilterField";
const useStyles = makeStyles((theme) => ({
  formControl: {
    minHeight: 80,
    padding: 0,
    marginRight: theme.shape.padding,
  },
  textButton: {
    width: 200,
    fontWeight: "bold",
    textTransform: "none",
    marginRight: theme.shape.padding,
    color: theme.palette.text.primary,
  },
  card: {
    marginBottom: theme.spacing(2),
  },
  dFlex: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.shape.padding,
  },
  dataSource: {
    "& input": {
      height: 28,
    },
  },
  marginTop: {
    marginTop: theme.shape.padding,
  },
  marginRight: {
    marginRight: theme.shape.padding,
  },
  fieldsMultiSelectAlert: {
    marginTop: theme.spacing(2),
  },
  exportAlert: {
    marginBottom: theme.shape.padding,
  },
  disabledMenuItem: {
    color: theme.palette.text.secondary,
  },
  tooltip: {
    boxShadow: theme.shadows[4],
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
  },
}));

const generateLogTypesObject = (orgLogTypes, allLogTypes) => {
  const orgLogTypesList = orgLogTypes.map((obj) => obj.name);
  const newLogTypesObj = {};
  for (let [key, value] of Object.entries(allLogTypes)) {
    if (orgLogTypesList.includes(key)) {
      newLogTypesObj[key] = value;
    }
  }
  return newLogTypesObj;
};

const isThereDataSourcesSelected = (queryControls) =>
  Object.values(queryControls).find((q) => q.field === "type") &&
  Object.values(queryControls).find((q) => q.field === "type").value.length > 0;

const isThereActiveQueryData = (activeQuery) =>
  activeQuery && activeQuery.length > 0;

const getNumberOfDataSources = (queryControls) =>
  Object.values(queryControls).find((q) => q.field === "type")
    ? Object.values(queryControls).find((q) => q.field === "type").value.length
    : 0;

export const populateAvailableFieldsList = (typeControl, logTypes) => {
  if (_.isNil(logTypes)) {
    return [];
  }
  if (_.isNil(typeControl) || typeControl.value.length === 0) {
    return _.flow(
      _.toPairs,
      (arr) => _.map(arr, (obj) => _.get(obj, ["1", "all_columns"], [])),
      _.flatten,
      _.uniq,
      (arr) => _.orderBy(arr, (elem) => elem.toLowerCase())
    )(logTypes);
  }

  return _(typeControl.value)
    .filter((obj) => obj in logTypes)
    .map((obj) => logTypes[obj].all_columns)
    .flatten()
    .uniq()
    .value();
};

const getColumnValue = (params, path) => _.get(params, path, []).join();

export const arrayReportColumnsMap = {
  additional_fields: {
    valueGetter: (params) =>
      getColumnValue(params, ["row", "additional_fields"]),
  },
  agent_name: {
    valueGetter: (params) => getColumnValue(params, ["row", "agent_name"]),
  },
  alliance_info: {
    valueGetter: (params) => getColumnValue(params, ["row", "alliance_info"]),
  },
  class: { valueGetter: (params) => getColumnValue(params, ["row", "class"]) },
  dns_ip: {
    valueGetter: (params) => getColumnValue(params, ["row", "dns_ip"]),
  },
  dns_ip_ipv4: {
    valueGetter: (params) => getColumnValue(params, ["row", "dns_ip_ipv4"]),
  },
  dns_ip_ipv6: {
    valueGetter: (params) => getColumnValue(params, ["row", "dns_ip_ipv6"]),
  },
  event_tags: {
    valueGetter: (params) => getColumnValue(params, ["row", "event_tags"]),
  },
  groups: {
    valueGetter: (params) => getColumnValue(params, ["row", "groups"]),
  },
  info: { valueGetter: (params) => getColumnValue(params, ["row", "info"]) },
  ip: { valueGetter: (params) => getColumnValue(params, ["row", "ip"]) },
  ip_flags: {
    valueGetter: (params) => getColumnValue(params, ["row", "ip_flags"]),
  },
  ip_ipv4: {
    valueGetter: (params) => getColumnValue(params, ["row", "ip_ipv4"]),
  },
  ip_ipv6: {
    valueGetter: (params) => getColumnValue(params, ["row", "ip_ipv6"]),
  },
  logged_on_user: {
    valueGetter: (params) => getColumnValue(params, ["row", "logged_on_user"]),
  },
  mac: { valueGetter: (params) => getColumnValue(params, ["row", "mac"]) },
  parameter: {
    valueGetter: (params) => getColumnValue(params, ["row", "parameter"]),
  },
  parts: { valueGetter: (params) => getColumnValue(params, ["row", "parts"]) },
  privileges: {
    valueGetter: (params) => getColumnValue(params, ["row", "privileges"]),
  },
  recipients: {
    valueGetter: (params) => getColumnValue(params, ["row", "recipients"]),
  },
  ref: { valueGetter: (params) => getColumnValue(params, ["row", "ref"]) },
  registry: {
    valueGetter: (params) => getColumnValue(params, ["row", "registry"]),
  },
  service_principal_names: {
    valueGetter: (params) =>
      getColumnValue(params, ["row", "service_principal_names"]),
  },
  sha256: {
    valueGetter: (params) => getColumnValue(params, ["row", "sha256"]),
  },
  targets: {
    valueGetter: (params) => getColumnValue(params, ["row", "targets"]),
  },
  tcp_flags: {
    valueGetter: (params) => getColumnValue(params, ["row", "tcp_flags"]),
  },
  threatInfo_indicators_applicationName: {
    valueGetter: (params) =>
      getColumnValue(params, ["row", "threatInfo_indicators_applicationName"]),
  },
  threatInfo_indicators_indicatorName: {
    valueGetter: (params) =>
      getColumnValue(params, ["row", "threatInfo_indicators_indicatorName"]),
  },
  threatInfo_indicators_sha256Hash: {
    valueGetter: (params) =>
      getColumnValue(params, ["row", "threatInfo_indicators_sha256Hash"]),
  },
  threats: {
    valueGetter: (params) => getColumnValue(params, ["row", "threats"]),
  },
  unknown_field: {
    valueGetter: (params) => getColumnValue(params, ["row", "unknown_field"]),
  },
  user_account_control: {
    valueGetter: (params) =>
      getColumnValue(params, ["row", "user_account_control"]),
  },
  zone: { valueGetter: (params) => getColumnValue(params, ["row", "zone"]) },
};
export const getColumns = (arrayReportColumnsMap = {}, resultControls) => {
  if (!("outputFields" in resultControls)) {
    return [];
  }

  return resultControls.outputFields.map((c) => {
    const baseColumnAttributes = {
      title: c,
      field: c,
      searchable: true,
      flex: 1,
      minWidth: 150,
    };
    if (c in arrayReportColumnsMap) {
      return { ...baseColumnAttributes, ...arrayReportColumnsMap[c] };
    }
    return baseColumnAttributes;
  });
};

export const filterColumns = (activeQuery = [], columns = []) =>
  activeQuery.length > 0
    ? columns.filter((column) =>
        activeQuery.some((obj) => !_.isNil(obj[column.field]))
      )
    : columns;

const populateOutputFieldsList = (
  firstElement,
  typeControl,
  logTypes,
  availableFieldsList,
  extraOutputFieldsList
) => {
  if (_.isNil(typeControl) || _.isNil(logTypes)) {
    return [];
  }

  const arr = _(typeControl.value)
    .filter((obj) => obj in logTypes)
    .map((obj) => logTypes[obj].core_columns)
    .flatten()
    // .push(...extraOutputFieldsList)
    .uniq()
    .filter((obj) => availableFieldsList.includes(obj))
    .value();
  return [firstElement, ...arr];
};

export const sortColumns = (sortByList, columnsList) => {
  if (Array.isArray(sortByList) && sortByList.length === 0) {
    return columnsList;
  }
  const differenceColumnsList = _.difference(
    columnsList.map((obj) => obj.field),
    sortByList
  );
  const newColumnsObj = _(differenceColumnsList)
    .groupBy((str) => str)
    .value();
  const [newColumnsList, oldColumnsList] = _(columnsList)
    .partition((obj) => obj.field in newColumnsObj)
    .value();

  return _(oldColumnsList)
    .sortBy((obj) => sortByList.indexOf(obj.field))
    .value()
    .concat(newColumnsList);
};

const Search = (props) => {
  const [isExporting, setIsExporting] = useState(false);
  const [delta, setDelta] = useState("custom");
  const [selectedModel, setSelectedModel] = useState(false);
  const [queryControls, queryControlInterface] =
    useQueryControls("activeQuery");
  const [resultControls, resultControlInterface] =
    useResultControls("activeQuery");
  const [addSuggestedFields, setAddSuggestedFields] = useState(true);
  const [saveOpen, setSaveOpen] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [formError, setFormError] = useState(false); // eslint-disable-line no-unused-vars
  const [anchorEl, setAnchorEl] = useState(false);
  const [extraOutputFieldsList, setExtraOutputFieldsList] = useState([]);
  const [logTypes, setLogTypes] = useState({});
  const [activeQuery, setActiveQuery] = useState([]);
  const [numberOfDataSources, setDataSourceNumber] = useState(0);
  const classes = useStyles();
  const [errorMessage, setErrorMessage] = useState("");
  const [isAlertVisible, setIsAlertVisible] = useState(true);
  const [availableFieldsList, setAvailableFieldsList] = useState([]);
  const [columnOrderList, setColumnOrderList] = useState([]);
  const availableFieldsObj = Schema.getSchema("/bq").schema.properties;
  const typeControl = Object.values(queryControls).find(
    (control) => control.field === "type" && control.operator === "in"
  );

  useEffect(() => {
    const newNumberofDataSources = getNumberOfDataSources(queryControls);
    setDataSourceNumber(newNumberofDataSources);
    if (numberOfDataSources !== newNumberofDataSources) {
      setActiveQuery([]);
    } else {
      setActiveQuery(props.activeQuery);
    }
  }, [queryControls, props.activeQuery]);

  useEffect(() => {
    const matchedQuery = props.savedQueries.find(
      (q) => q.id === Number(props.queryID)
    );
    if (matchedQuery) {
      if (!selectedModel || selectedModel.id !== matchedQuery.id) {
        setAddSuggestedFields(false);
        setSelectedModel(matchedQuery);
      }
    }
  }, [props.queryID, props.savedQueries]);

  useEffect(() => {
    if (props.orgLogTypes) {
      const orgLogTypes = generateLogTypesObject(
        props.orgLogTypes,
        props.logTypes
      );
      setLogTypes(orgLogTypes);
    }
  }, [props.orgLogTypes]);

  useEffect(() => {
    if (selectedModel) {
      const updatedUUIDs = [];
      const toAddQPs = [];
      const loadingQueryParams = [
        ...selectedModel.queryParams.filter((qp) => !Array.isArray(qp)),
        { field: "query.id", value: selectedModel.id },
      ];
      const loadingOrQueryParams = [
        ...selectedModel.queryParams.filter((qp) => Array.isArray(qp)),
      ];
      const overloadFields = [];

      if (selectedModel.id === Number(props.queryID) && props.query) {
        Object.keys(props.query).forEach((k) => {
          const qp = { ...decodeKey(k), value: props.query[k] };
          overloadFields.push(qp.field);
          toAddQPs.push(qp);
        });
      }

      loadingQueryParams.forEach((selectedQP) => {
        var matched = false;
        Object.keys(queryControls).forEach((qcUUID) => {
          if (
            queryControls[qcUUID].field === selectedQP.field &&
            queryControls[qcUUID].operator === selectedQP.operator &&
            !!queryControls[qcUUID].negate === !!selectedQP.negate
          ) {
            queryControlInterface.update(qcUUID, { value: selectedQP.value });
            matched = qcUUID;
          }
        });
        if (matched) {
          updatedUUIDs.push(matched);
        } else if (!overloadFields.includes(selectedQP.field)) {
          toAddQPs.push(selectedQP);
        }
      });

      Object.keys(queryControls)
        .filter(
          (qcUUID) =>
            !updatedUUIDs.includes(qcUUID) ||
            overloadFields.includes(queryControls[qcUUID].field)
        )
        .map((qcUUID) => queryControlInterface.remove(qcUUID));
      Object.keys(resultControls).map((resultType) =>
        resultControlInterface.remove(resultType)
      );
      // add values from selectedModel
      toAddQPs.map((qp) => queryControlInterface.add(qp));
      loadingOrQueryParams.forEach((orParam) => {
        queryControlInterface.add(orParam);
      });
      Object.keys(selectedModel.resultParams).map((resultName) =>
        resultControlInterface.update(
          resultName,
          selectedModel.resultParams[resultName]
        )
      );

      props.reload();
    }
  }, [selectedModel, props.query, props.queryID]);

  useDeepCompareEffect(() => {
    const availableFieldsList = populateAvailableFieldsList(
      typeControl,
      logTypes
    );
    setAvailableFieldsList(availableFieldsList);
    if (typeControl && addSuggestedFields && props.ready) {
      const firstElem = !isDistinct() ? "id" : "grouped_count";
      const outputFields = populateOutputFieldsList(
        firstElem,
        typeControl,
        logTypes,
        availableFieldsList,
        extraOutputFieldsList
      );
      resultControlInterface.update("outputFields", outputFields);
    }
  }, [typeControl, props.orgLogTypes, addSuggestedFields]);

  const handleAddExtraOutputField = (currentOutputFields) => {
    setExtraOutputFieldsList(
      _.difference(currentOutputFields, resultControls.outputFields)
    );
  };
  const handleRestore = () => {
    setDelta("custom");
    setSelectedModel(false);
    queryControlInterface.restore();
    resultControlInterface.restore();
  };

  const handleSelectModel = (model) => {
    setAddSuggestedFields(false);
    setSelectedModel(model);
  };

  const handleSelectAllTypes = () => {
    const typeControl = Object.values(queryControls).find(
      (control) => control.field === "type" && control.operator === "in"
    );
    const value = Object.keys(logTypes).filter(
      (logType) => props.language[logType]
    );
    if (typeControl) {
      setAddSuggestedFields(false);
      queryControlInterface.update(typeControl.uuid, { value });
    }
  };

  const handleSubmit = () => {
    props.reload("activeQuery");
  };

  const onSave = (queryID) => {
    setSaveOpen(false);
    props.reload(true);
  };

  const checkMenuDisabled = (contextMenu) => {
    if (isDateTimeField(availableFieldsObj[contextMenu.fieldName])) {
      return true;
    }
    if (
      contextMenu.fieldName &&
      resultControls.outputFields?.find(
        (field) => field === contextMenu.fieldName
      )
    ) {
      return false;
    }
    return true;
  };

  // Context Menu Functions
  const menuCopy = (contextMenu) => {
    copyTextToClipboard(contextMenu.model[contextMenu.fieldName]);
  };

  const menuFilterEq = (contextMenu) => {
    queryControlInterface.add({
      field: contextMenu.fieldName,
      value: getValue(null, contextMenu.model[contextMenu.fieldName]),
      operator: "eq",
      negate: false,
    });
  };

  const menuFilterNeq = (contextMenu) => {
    queryControlInterface.add({
      field: contextMenu.fieldName,
      value: getValue(null, contextMenu.model[contextMenu.fieldName]),
      operator: "eq",
      negate: true,
    });
  };

  /*
    description: The type of output that the report processing will produce; "direct" ==
      list of output objects in data field of response. Else, "jsonl", "csv",
      "xlsx", which produce signed URL to output file of given type.
  */
  const onExport = async (downloadType) => {
    const transform = !resultControls.transform
      ? []
      : resultControls.transform.map((obj) => {
          delete obj.uuid;
          delete obj.boolParam;
          return obj;
        });
    const queryParams = Object.values(queryControls).map((obj) => obj);
    const request = new Request("/bq", queryParams, {
      ...resultControls,
      transform,
      outputType: downloadType,
    });

    // set state var to disable export options
    // and display loading indicator in UI
    setIsExporting(true);

    // async to prohibit multiple export clicks
    // while we work on exporting
    request
      .get()
      .then(() => setIsExporting(false))
      .catch((err) => {
        setIsExporting(false);
        handleError("We had an issue exporting your report.");
      });
  };

  // These functions are to support the legacy UI "distint" action
  // it will be replaced by a robust result UI in the future
  const isDistinct = () => {
    return resultControls.transform?.find((t) => t.operator === "count");
  };

  const handleDistinct = () => {
    const control = isDistinct();
    const existingOrderBy = _.get(resultControls, ["orderBy"], []);
    const existingOutputFields = _.get(resultControls, ["outputFields"], []);
    if (control) {
      const updated = resultControls.transform
        .filter((t) => t.uuid !== control.uuid)
        .map((t) => {
          return {
            field: t.field,
            operator: t.operator,
            negate: t.negate,
            value: t.value,
          };
        });
      resultControlInterface.update("transform", updated);
      resultControlInterface.update("outputFields", [
        "id",
        "Timestamp",
        ...existingOutputFields.filter((obj) => obj !== "grouped_count"),
      ]);
      resultControlInterface.update("orderBy", [
        ...existingOrderBy.filter((obj) => obj.key !== "grouped_count"),
      ]);
    } else {
      let transformToMap = resultControls?.transform?.length
        ? resultControls.transform
        : [];
      const updated = transformToMap.map((t) => {
        return {
          field: t.field,
          operator: t.operator,
          negate: t.negate,
          value: t.value,
        };
      });
      updated.push({ operator: "count", value: "grouped_count" });
      resultControlInterface.update("transform", updated);
      resultControlInterface.update("outputFields", [
        "grouped_count",
        ...existingOutputFields.filter(
          (obj) => obj !== "id" && obj !== "Timestamp"
        ),
      ]);
    }
  };

  const toggleMoreMenu = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const getFields = (resultControls) =>
    _.isNil(resultControls.outputFields)
      ? []
      : resultControls.outputFields
          .filter((field) => field in availableFieldsObj)
          .map((c) => ({ field: c }));

  const handleError = (resp) => {
    if (resp !== "") {
      setErrorMessage(resp);
      setIsAlertVisible(true);
    }
  };

  const handleAlertClose = () => {
    setErrorMessage("");
    setIsAlertVisible(false);
  };

  const filterActions = [
    {
      label: "Done",
      onClick: (editorValues) => {
        if (editorValues.uuid) {
          queryControlInterface.update(editorValues.uuid, editorValues);
        } else {
          queryControlInterface.add(editorValues);
        }
      },
    },
    {
      label: "Cancel",
      onClick: (editorValues) => {
        // noop.  Just closes editor
      },
    },
  ];

  const handleColumnOrderChange = (newColumnOrder) => {
    setColumnOrderList(newColumnOrder);
  };

  const columns = getColumns(arrayReportColumnsMap, resultControls);
  const filteredColumns = useMemo(
    () => filterColumns(activeQuery, columns),
    [activeQuery, columns]
  );
  const sortedFilteredColumns = sortColumns(columnOrderList, filteredColumns);

  return (
    <div>
      <SavedQueryModal
        show={showSaved}
        onClick={handleSelectModel}
        queries={props.savedQueries}
        onClose={() => setShowSaved(false)}
      />
      <SaveRequestModal
        open={saveOpen}
        refName="activeQuery"
        onClose={onSave}
        savedQueries={props.savedQueries}
      />
      {isAlertVisible && errorMessage && (
        <Alert
          severity="error"
          className={classes.exportAlert}
          onClose={handleAlertClose}
        >
          {errorMessage}
        </Alert>
      )}
      {selectedModel.id && (
        <Typography color="textSecondary" gutterBottom>
          Working with saved report: {selectedModel.name}
        </Typography>
      )}
      <Grid container>
        {formError && (
          <Grid item xs={12}>
            <Typography color="error">{formError}</Typography>
          </Grid>
        )}
        <Grid className={classes.dFlex} item form="maincomponent" xs>
          <TimeControls
            delta={delta}
            setDelta={setDelta}
            controls={queryControls}
            interface={queryControlInterface}
          />
          <FormControl fullWidth className={classes.formControl}>
            <DataSourcePicker
              controls={queryControls}
              interface={queryControlInterface}
              logTypes={logTypes}
              language={props.language}
              errorMsg={(resp) => handleError(resp)}
            />
          </FormControl>
          <Button
            className={classes.textButton}
            onClick={() => setShowAdvanced(!showAdvanced)}
            datacy={"reportShowAdvancedBtn"}
          >
            {showAdvanced ? "Hide" : "Show"} Advanced
          </Button>
          <ButtonGroup
            className={classes.marginRight}
            color="primary"
            variant="contained"
          >
            <Button
              startIcon={<PlayCircleOutlineIcon />}
              onClick={handleSubmit}
              datacy={"reportSubmitButton"}
            >
              Submit
            </Button>
          </ButtonGroup>
          <div>
            <IconButton
              onClick={toggleMoreMenu}
              aria-controls={"simple-menu"}
              data-cy={"moreReportBtnMenu"}
            >
              <MoreHorizIcon />
            </IconButton>
            <Menu
              keepMounted
              id={"simple-menu"}
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              disableScrollLock={true}
              onClose={() => setAnchorEl(null)}
            >
              {/* 
                  if we are actively exporting, show a dummy item that explains to the user we are exporting 
                  otherwise, if we are not actively exporting, we can show them export options 
                */}
              {isExporting ? (
                <Tooltip
                  title={"Exporting could take up to several minutes."}
                  placement={"left"}
                  classes={{ tooltip: classes.tooltip }}
                >
                  <MenuItem className={classes.disabledMenuItem}>
                    Exporting...
                  </MenuItem>
                </Tooltip>
              ) : (
                <MenuItem
                  onClick={() => onExport("jsonl")}
                  disabled={!isThereActiveQueryData(activeQuery) || isExporting}
                >
                  Export JSONL
                </MenuItem>
              )}
              {!isExporting && (
                <MenuItem
                  onClick={() => onExport("csv")}
                  disabled={!isThereActiveQueryData(activeQuery) || isExporting}
                >
                  Export CSV
                </MenuItem>
              )}
              <LicenseRestriction requires={"query.create"}>
                <MenuItem
                  onClick={() => {
                    setSaveOpen(true);
                    setAnchorEl(null);
                  }}
                  data-cy={"saveReportBtn"}
                  disabled={!isThereDataSourcesSelected(queryControls)}
                >
                  Save & Schedule Report
                </MenuItem>
              </LicenseRestriction>
              <MenuItem
                onClick={() => {
                  handleRestore();
                  setAnchorEl(null);
                }}
              >
                Reset Report
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setShowSaved(true);
                  setAnchorEl(null);
                }}
                datacy={"loadSavedReportBtn"}
              >
                Load Saved Report
              </MenuItem>
            </Menu>
          </div>
        </Grid>
      </Grid>
      <Card className={classes.card}>
        <Collapse in={showAdvanced} timeout="auto" unmountOnExit>
          <CardContent>
            <FormControl fullWidth>
              <OutputFieldsEditor
                control={resultControls.outputFields}
                interface={resultControlInterface}
                fields={availableFieldsList}
                distinctCountsApplied={!!isDistinct()}
                onAddExtraOutputField={handleAddExtraOutputField}
              />
            </FormControl>
            {isAlertVisible && errorMessage && (
              <Alert
                severity="error"
                className={classes.fieldsMultiSelectAlert}
                onClose={handleAlertClose}
              >
                {errorMessage}
              </Alert>
            )}
            <div className={classes.marginTop}>
              <FilterEditor
                controls={queryControls}
                interface={queryControlInterface}
                actions={filterActions}
                fields={getFields(resultControls)}
              />
            </div>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!isDistinct()}
                    onChange={handleDistinct}
                    color="primary"
                  />
                }
                label="Apply Distinct Counts to Report"
                datacy={"reportDistinctCountToggle"}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={addSuggestedFields}
                    onChange={(e) => setAddSuggestedFields(!addSuggestedFields)}
                    color="primary"
                  />
                }
                label="Add Suggested Fields"
              />
            </FormGroup>
            {/* resultControls.transform?.map(resultParam=>(<TransformParamControl key={resultParam.uuid} refName="activeQuery" param={resultParam.uuid} label={resultParam.field} />)) */}
            <Button variant={"outlined"} onClick={handleSelectAllTypes}>
              Select All Data Sources
            </Button>
          </CardContent>
        </Collapse>
      </Card>
      {activeQuery && activeQuery.length === 5000 && (
        <Alert severity="warning">
          Your search exceeded the maximum number of rows (5000)
        </Alert>
      )}
      <DataTable
        title="Results"
        data={activeQuery}
        onColumnOrderChange={handleColumnOrderChange}
        columns={sortedFilteredColumns}
        isFetching={!props.ready}
        contextMenuItems={[
          {
            onClick: menuCopy,
            text: "Copy to Clipboard",
            isDisabled: checkMenuDisabled,
          },
          {
            onClick: menuFilterEq,
            text: "Add to filters",
            isDisabled: checkMenuDisabled,
          },
          {
            onClick: menuFilterNeq,
            text: "Remove from results",
            isDisabled: checkMenuDisabled,
          },
        ]}
      />
    </div>
  );
};

// ** Proptypes ***********************
Search.propTypes = {
  results: PropTypes.array,
  columns: PropTypes.array,
  dispatch: PropTypes.func.isRequired,
  orgId: PropTypes.string.isRequired,
  savedQueries: PropTypes.array,
};

// ** Default props *******************
Search.defaultProps = {
  results: [],
  columns: [],
  logTypes: {},
  savedQueries: [],
};

const mapDispatchToProps = (dispatch) => {
  return {
    reload: (force) => dispatch(loadPageData(force)),
  };
};

// ** State constants *****************
const mapStateToProps = (state) => {
  return {
    orgId: state.location.payload.orgId,
    query: state.location.query,
    queryID: state.location.payload.id1,
    logTypes: Codex.constant.api.bq.type,
    language: Codex.language.models.bq.type,
  };
};

const SearchPage = connect(mapStateToProps, mapDispatchToProps)(Search);
export default SearchPage;
